/**
 * useMerchantCoupons Hook - Aggregator Pattern Implementation
 *
 * This hook aggregates merchant coupon data:
 * - List of merchant coupons from API
 * - Categorized coupons by status
 * - Computed statistics and metrics
 *
 * Pattern: AGGREGATOR
 * - Fetches and provides coupon data
 * - Computes coupon statistics
 * - Categorizes by status and validity
 * - Single loading/error state
 *
 * IMPORTANT: This hook only reads coupon data. For coupon creation,
 * updates, or deletion, use the useMerchantCouponActions coordinator hook.
 */

import { useListCoupons } from '@/generated/hooks';
import { TimestampService } from '@zlinkx/shared-v2';
import type { ListCouponsQueryType } from '@zlinkx/shared-v2';
import type { AggregatorPattern } from '../../types/hook-patterns.types';
import type { MerchantCoupon } from '@/types/common.types';
import type { MerchantCouponsData, MerchantCouponsComputed, UseMerchantCouponsOptions } from '@/types/controller.types';
import { transformMerchantCoupons } from '@/utils/api-transformers';
import type { TransformedMerchantCoupon } from '@/utils/api-transformers';

// ============================================================================
// TYPES
// ============================================================================

// All types are now imported from @/types/common.types

// Type extensions are defined in controller.types.ts

// Return type is defined in controller.types.ts
const DEFAULT_COUPON_LIMIT: ListCouponsQueryType['limit'] = 50;

// ============================================================================
// HELPER FUNCTIONS (Pure)
// ============================================================================

/**
 * Check if a coupon is expired
 */
function isCouponExpired(expiryDate: string | null): boolean {
	if (expiryDate === null || expiryDate.length === 0) return false;
	const expiry = TimestampService.parseDateTimeInput(expiryDate);
	if (!TimestampService.isValidDate(expiry)) {
		return false;
	}
	return TimestampService.getMillisecondsSince(expiry) > 0;
}

/**
 * Calculate days until expiry
 */
function calculateDaysUntilExpiry(expiryDate: string | null): number | null {
	if (expiryDate === null || expiryDate.length === 0) return null;
	const expiry = TimestampService.parseDateTimeInput(expiryDate);
	if (!TimestampService.isValidDate(expiry)) {
		return null;
	}
	const diffMs = -TimestampService.getMillisecondsSince(expiry);
	const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

	return diffDays > 0 ? diffDays : 0;
}

/**
 * Check if coupon is expiring soon (within 7 days)
 */
function isExpiringSoon(expiryDate: string | null): boolean {
	const days = calculateDaysUntilExpiry(expiryDate);
	return days !== null && days > 0 && days <= 7;
}

function isServerCouponStatus(
	value: NonNullable<UseMerchantCouponsOptions['filters']>['status'],
): value is ListCouponsQueryType['status'] {
	return value === 'active' || value === 'expired';
}

function toMillis(value: string | null, fallback: number): number {
	if (value === null || value.length === 0) return fallback;
	const parsed = TimestampService.parseDateTimeInput(value);
	if (!TimestampService.isValidDate(parsed)) {
		return fallback;
	}
	return TimestampService.now() - TimestampService.getMillisecondsSince(parsed);
}

function sortCoupons(coupons: TransformedMerchantCoupon[], sortBy?: string): TransformedMerchantCoupon[] {
	if (sortBy === undefined || sortBy.length === 0) return coupons;

	const sorted = [...coupons];
	if (sortBy === 'created') {
		sorted.sort((a, b) => toMillis(b.couponCreatedAt, 0) - toMillis(a.couponCreatedAt, 0));
		return sorted;
	}

	if (sortBy === 'expires') {
		sorted.sort(
			(a, b) =>
				toMillis(a.couponExpiredAt, Number.MAX_SAFE_INTEGER) -
				toMillis(b.couponExpiredAt, Number.MAX_SAFE_INTEGER),
		);
		return sorted;
	}

	if (sortBy === 'title') {
		sorted.sort((a, b) => a.couponTitle.localeCompare(b.couponTitle));
		return sorted;
	}

	return coupons;
}

function applyClientFilters(
	coupons: TransformedMerchantCoupon[],
	filters: UseMerchantCouponsOptions['filters'],
): TransformedMerchantCoupon[] {
	const status = filters?.status;
	let filtered = coupons;

	if (status === 'active') {
		filtered = filtered.filter(c => c.couponIsActive && !isCouponExpired(c.couponExpiredAt));
	} else if (status === 'expired') {
		filtered = filtered.filter(c => isCouponExpired(c.couponExpiredAt));
	}

	const search = filters?.search?.trim().toLowerCase();
	if (search !== undefined && search.length > 0) {
		filtered = filtered.filter(c => {
			const title = c.couponTitle.toLowerCase();
			const description = c.couponDescription.toLowerCase();
			return title.includes(search) || description.includes(search);
		});
	}

	return sortCoupons(filtered, filters?.sortBy);
}

// Note: Redemption tracking properties (currentRedemptions, maxRedemptions)
// are not included in the backend response and would need to be added
// to the contract if needed

// No enrichment needed - using backend response directly
// MerchantCoupon type is an alias for GetMerchantCouponsResponseType[0]

/**
 * Calculate average discount
 */
function calculateAverageDiscount(coupons: TransformedMerchantCoupon[]): number {
	if (coupons.length === 0) return 0;

	const percentageCoupons = coupons.filter(c => c.couponType === 'percentage');
	if (percentageCoupons.length === 0) return 0;

	const total = percentageCoupons.reduce((sum, c) => sum + c.couponValue, 0);
	return Math.round(total / percentageCoupons.length);
}

/**
 * Find newest coupon
 */
function findNewestCoupon(coupons: TransformedMerchantCoupon[]): TransformedMerchantCoupon | null {
	if (coupons.length === 0) return null;

	return coupons.reduce(
		(newest, current) => {
			if (
				newest === null ||
				toMillis(current.couponCreatedAt, 0) > toMillis(newest.couponCreatedAt, 0)
			) {
				return current;
			}
			return newest;
		},
		null as TransformedMerchantCoupon | null,
	);
}

/**
 * Find oldest active coupon
 */
function findOldestActiveCoupon(coupons: TransformedMerchantCoupon[]): TransformedMerchantCoupon | null {
	const activeCoupons = coupons.filter(c => c.couponIsActive && !isCouponExpired(c.couponExpiredAt));
	if (activeCoupons.length === 0) return null;

	return activeCoupons.reduce(
		(oldest, current) => {
			if (
				oldest === null ||
				toMillis(current.couponCreatedAt, Number.MAX_SAFE_INTEGER) <
					toMillis(oldest.couponCreatedAt, Number.MAX_SAFE_INTEGER)
			) {
				return current;
			}
			return oldest;
		},
		null as TransformedMerchantCoupon | null,
	);
}

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

/**
 * Aggregates merchant coupon data with statistics
 *
 * @example
 * ```tsx
 * function MerchantCouponsPage() {
 *   const coupons = useMerchantCoupons(
 *     selectedMerchantId,
 *     {
 *       filters: {
 *         status: 'active',
 *         search: 'summer',
 *         sortBy: 'created',
 *       },
 *     }
 *   );
 *
 *   if (coupons.isLoading) return <LoadingSpinner />;
 *   if (coupons.error) return <ErrorMessage error={coupons.error} />;
 *
 *   return (
 *     <div>
 *       <CouponStats
 *         total={coupons.computed.totalCount}
 *         active={coupons.computed.activeCount}
 *         redemptionRate={coupons.computed.overallRedemptionRate}
 *       />
 *       <CouponList
 *         coupons={coupons.data.activeCoupons}
 *         expiringSoon={coupons.data.expiringSoonCoupons}
 *       />
 *     </div>
 *   );
 * }
 * ```
 */
export function useMerchantCoupons(
	merchantId: number,
	options: UseMerchantCouponsOptions = {},
): AggregatorPattern<MerchantCouponsData, MerchantCouponsComputed> & {
	findCouponById: (couponId: number) => MerchantCoupon | null;
	findCouponByCode: (code: string) => MerchantCoupon | null;
	filterByStatus: (status: string) => MerchantCoupon[];
	hasNextPage: boolean;
	fetchNextPage: () => void;
	isFetchingNextPage: boolean;
} {
	const { enabled = true, filters } = options;

	// Convert filters to query params
	const queryParams: ListCouponsQueryType = {
		limit: DEFAULT_COUPON_LIMIT,
	};
	const selectedStatus = filters?.status;
	if (isServerCouponStatus(selectedStatus)) {
		queryParams.status = selectedStatus;
	}

	// ========================================
	// Fetch Coupon Data
	// ========================================

	const couponsQuery = useListCoupons(
		{ merchantId, query: queryParams },
		{
			enabled,
		},
	);

	// ========================================
	// Process and Enrich Data
	// ========================================

	const rawCoupons = couponsQuery.data ?? [];

	// Transform backend response to UI-expected format
	const transformedCoupons = applyClientFilters(transformMerchantCoupons(rawCoupons), filters);

	// Categorize coupons based on actual backend properties
	const activeCoupons = transformedCoupons.filter(c => c.couponIsActive && !isCouponExpired(c.couponExpiredAt));
	const expiredCoupons = transformedCoupons.filter(c => isCouponExpired(c.couponExpiredAt));
	const expiringSoonCoupons = activeCoupons.filter(c => isExpiringSoon(c.couponExpiredAt));

	// ========================================
	// Raw Data
	// ========================================

	const data: MerchantCouponsData = {
		coupons: transformedCoupons,
		activeCoupons,
		expiredCoupons,
		fullyRedeemedCoupons: [], // Redemption count not available in backend response
		expiringSoonCoupons,
	};

	// ========================================
	// Computed Values
	// ========================================

	const computed: MerchantCouponsComputed = {
		totalCount: transformedCoupons.length,
		activeCount: activeCoupons.length,
		expiredCount: expiredCoupons.length,

		// Redemption metrics - not available without currentRedemptions/maxRedemptions
		totalRedemptions: 0,
		totalPotentialRedemptions: 0,
		overallRedemptionRate: 0,

		// Discount metrics
		averageDiscount: calculateAverageDiscount(transformedCoupons),

		// Popular coupons - changed to newest/oldest since redemption data not available
		mostPopularCoupon: findNewestCoupon(transformedCoupons),
		leastPopularCoupon: findOldestActiveCoupon(transformedCoupons),

		// Flags
		hasCoupons: transformedCoupons.length > 0,
		hasActiveCoupons: activeCoupons.length > 0,
		hasExpiringSoon: expiringSoonCoupons.length > 0,

		// Attention needed
		needsAttentionCount: expiringSoonCoupons.length + expiredCoupons.length,
	};

	// ========================================
	// Helper Functions
	// ========================================

	const findCouponById = (couponId: number): MerchantCoupon | null => {
		return transformedCoupons.find(c => c.couponId === couponId) || null;
	};

	const findCouponByCode = (code: string): MerchantCoupon | null => {
		return transformedCoupons.find(c => c.couponTitle === code) || null; // Using title as code not in response
	};

	const filterByStatus = (status: string): MerchantCoupon[] => {
		if (status === 'active') return activeCoupons;
		if (status === 'expired') return expiredCoupons;
		return [];
	};

	// ========================================
	// Return Aggregator Pattern
	// ========================================
	return {
		data,
		computed,
		isLoading: couponsQuery.isLoading,
		error: couponsQuery.error ? 'Failed to load coupons' : null,

		// Pagination (from paginated query hook)
		hasNextPage: couponsQuery.hasNextPage,
		fetchNextPage: couponsQuery.fetchNextPage,
		isFetchingNextPage: couponsQuery.isFetchingNextPage,

		// Additional helpers
		findCouponById,
		findCouponByCode,
		filterByStatus,
	};
}

// ============================================================================
// EXPORTS
// ============================================================================

export default useMerchantCoupons;
